import { ref } from 'vue'
import { db, auth } from '@/firebase/firebase'
import {
  doc,
  getDoc,
  setDoc,
  deleteDoc,
  collection,
  query,
  where,
  getDocs
} from 'firebase/firestore'

/**
 * 찜한 현장 관리 Composable
 * - 로그인 사용자: Firestore에 저장
 * - 비로그인 사용자: localStorage에 저장 (로그인 시 동기화 가능)
 */

const STORAGE_KEY = 'mulgae_favorites'

export function useFavorite() {
  const favorites = ref([])
  const loading = ref(false)

  /**
   * 현재 사용자 UID 가져오기
   */
  const getCurrentUserId = () => {
    return auth.currentUser?.uid || null
  }

  /**
   * localStorage에서 찜 목록 가져오기
   */
  const getLocalFavorites = () => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY)
      return stored ? JSON.parse(stored) : []
    } catch {
      return []
    }
  }

  /**
   * localStorage에 찜 목록 저장
   */
  const setLocalFavorites = (ids) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(ids))
    } catch (e) {
      console.error('찜 목록 저장 실패:', e)
    }
  }

  /**
   * 찜 목록 로드
   */
  const loadFavorites = async () => {
    const userId = getCurrentUserId()

    if (!userId) {
      favorites.value = getLocalFavorites()
      return favorites.value
    }

    loading.value = true
    try {
      const q = query(
        collection(db, 'favorites'),
        where('userId', '==', userId)
      )
      const snapshot = await getDocs(q)
      favorites.value = snapshot.docs.map((doc) => doc.data().propertyId)
      return favorites.value
    } catch (e) {
      console.error('찜 목록 로드 실패:', e)
      return []
    } finally {
      loading.value = false
    }
  }

  /**
   * 찜 추가
   * @param {string} propertyId 현장 ID
   */
  const addFavorite = async (propertyId) => {
    const userId = getCurrentUserId()

    if (!userId) {
      // 비로그인: localStorage에 저장
      const localFavs = getLocalFavorites()
      if (!localFavs.includes(propertyId)) {
        localFavs.push(propertyId)
        setLocalFavorites(localFavs)
        favorites.value = localFavs
      }
      return { success: true }
    }

    try {
      const docId = `${userId}_${propertyId}`
      await setDoc(doc(db, 'favorites', docId), {
        userId,
        propertyId,
        createdAt: new Date().toISOString()
      })

      if (!favorites.value.includes(propertyId)) {
        favorites.value.push(propertyId)
      }

      return { success: true }
    } catch (e) {
      console.error('찜 추가 실패:', e)
      return { success: false, error: e.message }
    }
  }

  /**
   * 찜 제거
   * @param {string} propertyId 현장 ID
   */
  const removeFavorite = async (propertyId) => {
    const userId = getCurrentUserId()

    if (!userId) {
      // 비로그인: localStorage에서 제거
      let localFavs = getLocalFavorites()
      localFavs = localFavs.filter((id) => id !== propertyId)
      setLocalFavorites(localFavs)
      favorites.value = localFavs
      return { success: true }
    }

    try {
      const docId = `${userId}_${propertyId}`
      await deleteDoc(doc(db, 'favorites', docId))
      favorites.value = favorites.value.filter((id) => id !== propertyId)
      return { success: true }
    } catch (e) {
      console.error('찜 제거 실패:', e)
      return { success: false, error: e.message }
    }
  }

  /**
   * 찜 토글
   * @param {string} propertyId 현장 ID
   */
  const toggleFavorite = async (propertyId) => {
    if (isFavorite(propertyId)) {
      return await removeFavorite(propertyId)
    } else {
      return await addFavorite(propertyId)
    }
  }

  /**
   * 찜 여부 확인
   * @param {string} propertyId 현장 ID
   */
  const isFavorite = (propertyId) => {
    return favorites.value.includes(propertyId)
  }

  /**
   * 찜 개수 가져오기
   */
  const getFavoriteCount = async () => {
    const userId = getCurrentUserId()

    if (!userId) {
      return getLocalFavorites().length
    }

    try {
      const q = query(
        collection(db, 'favorites'),
        where('userId', '==', userId)
      )
      const snapshot = await getDocs(q)
      return snapshot.size
    } catch {
      return 0
    }
  }

  /**
   * 로그인 시 localStorage 찜 목록을 Firestore로 동기화
   */
  const syncLocalToFirestore = async () => {
    const userId = getCurrentUserId()
    if (!userId) return

    const localFavs = getLocalFavorites()
    if (localFavs.length === 0) return

    try {
      for (const propertyId of localFavs) {
        const docId = `${userId}_${propertyId}`
        const docRef = doc(db, 'favorites', docId)
        const docSnap = await getDoc(docRef)

        if (!docSnap.exists()) {
          await setDoc(docRef, {
            userId,
            propertyId,
            createdAt: new Date().toISOString()
          })
        }
      }

      // 동기화 완료 후 localStorage 비우기
      localStorage.removeItem(STORAGE_KEY)
    } catch (e) {
      console.error('찜 목록 동기화 실패:', e)
    }
  }

  return {
    favorites,
    loading,
    loadFavorites,
    addFavorite,
    removeFavorite,
    toggleFavorite,
    isFavorite,
    getFavoriteCount,
    syncLocalToFirestore
  }
}
